import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useState } from "react";
import { getNewsletterArticle, sendSampleNewsletter } from "@/lib/newsletter.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { EMAIL_ERROR, isValidEmail } from "@/lib/validate";

const articleQuery = (slug: string) =>
  queryOptions({
    queryKey: ["newsletter", slug],
    queryFn: async () => {
      const res = await getNewsletterArticle({ data: { slug } });
      return res;
    },
  });

export const Route = createFileRoute("/newsletter/$slug")({
  loader: async ({ context, params }) => {
    const res = await context.queryClient.ensureQueryData(articleQuery(params.slug));
    if (!res) throw notFound();
  },
  head: ({ loaderData, params }) => ({
    meta: [
      { title: `Aesthetic Insights — ${params.slug}` },
      { name: "description", content: "Aesthetic Insights newsletter preview." },
    ],
  }),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <Button
          className="mt-6"
          onClick={() => {
            reset();
            router.invalidate();
          }}
        >
          Retry
        </Button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold">Article not found</h1>
      <Link to="/news" className="mt-4 inline-block text-link underline">
        Back to Aesthetic Insights
      </Link>
    </div>
  ),
  component: NewsletterPage,
});

function NewsletterPage() {
  return (
    <Suspense fallback={<div className="mx-auto max-w-3xl px-4 py-16 text-sm text-muted-foreground">Loading newsletter…</div>}>
      <NewsletterContent />
    </Suspense>
  );
}

function NewsletterContent() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(articleQuery(slug));
  if (!data) return null;
  const { article, html } = data;

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const onDownload = () => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aesthetic-insights-${article.slug}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setSending(true);
    try {
      await sendSampleNewsletter({ data: { slug, email } });
      toast.success(`Sample sent to ${email}`);
      setEmail("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send sample");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 pb-16">
      {/* Toolbar */}
      <div className="sticky top-[52px] z-30 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Link to="/news" className="text-sm text-muted-foreground hover:text-foreground">
              ← Insights
            </Link>
            <span className="hidden text-xs uppercase tracking-widest text-muted-foreground sm:inline">
              Newsletter preview
            </span>
          </div>
          <form onSubmit={onSend} noValidate className="flex flex-wrap items-end gap-2">
            <div className="min-w-[220px] flex-1">
              <Label htmlFor="sample-email" className="sr-only">
                Send sample to
              </Label>
              <Input
                id="sample-email"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? "sample-email-error" : undefined}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
              />
              {emailError && (
                <p id="sample-email-error" className="mt-1 text-xs text-destructive">
                  {emailError}
                </p>
              )}
            </div>
            <Button type="submit" disabled={sending}>
              {sending ? "Sending…" : "Send sample"}
            </Button>
            <Button type="button" variant="outline" onClick={onDownload}>
              Download .html
            </Button>
          </form>
        </div>
      </div>

      {/* Email preview iframe — renders exactly what would be sent */}
      <div className="mx-auto max-w-5xl px-4 pt-8">
        <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
          <iframe
            title="Newsletter preview"
            srcDoc={html}
            className="block h-[1400px] w-full"
            sandbox=""
          />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          This is the exact HTML that will be emailed — table-based layout with inline
          styles for high-fidelity rendering in Gmail, Outlook, and Apple Mail. Use
          “Download .html” to load it into a bulk email tool such as Mailchimp,
          HubSpot, or Constant Contact.
        </p>
      </div>
    </div>
  );
}
